import React, { useEffect, useState } from 'react';
import api from '../api';
import { STATUSES, MACHINES, MATERIAL_STATUSES } from './statusBadge';

const empty = {
  part_number: '', customer_id: '', po_number: '', due_date: '', machine_id: '',
  product_style: '', qty_ordered: '', qty_shipped: 0, sales_price: '',
  raw_material_status: 'Need To Be Ordered', status: 'In Queue', notes: '', msf: 0,
  archive: 'Active',
};

export default function AddOrder({ role }) {
  const [form, setForm] = useState(empty);
  const [customers, setCustomers] = useState([]);
  const [machines, setMachines] = useState([]);
  const [parts, setParts] = useState([]);
  const [selectedPartId, setSelectedPartId] = useState(null);
  const [priceWarning, setPriceWarning] = useState('');
  const [msg, setMsg] = useState('');
  const showPricing = role !== 'shop_floor';

  useEffect(() => {
    api.get('/api/customers').then(r => setCustomers(r.data || []));
    api.get('/api/machines').then(r => setMachines(r.data || []));
  }, []);

  useEffect(() => {
    if (!form.customer_id) { setParts([]); return; }
    api.get(`/api/parts?customer_id=${form.customer_id}`).then(r => setParts(r.data || []));
  }, [form.customer_id]);

  const fetchExactPrice = async (partId, qty) => {
    const q = parseInt(qty, 10);
    if (!partId || !Number.isFinite(q) || q <= 0) {
      setPriceWarning('');
      return;
    }
    try {
      const r = await api.get(`/api/parts/${partId}/price?qty=${q}`);
      setForm(f => ({ ...f, sales_price: String(Number(r.data.price).toFixed(4)) }));
      setPriceWarning('');
    } catch (err) {
      if (err?.response?.status === 404) {
        setForm(f => ({ ...f, sales_price: '' }));
        setPriceWarning('No saved price for this quantity');
      }
    }
  };

  const selectPart = (partNumber) => {
    const part = parts.find(p => p.part_number === partNumber);
    const pid = part?.id || null;
    setSelectedPartId(pid);
    setForm(f => ({ ...f, part_number: partNumber, product_style: part?.product_style || '' }));
    fetchExactPrice(pid, form.qty_ordered);
  };

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const updQty = (v) => {
    setForm(f => ({ ...f, qty_ordered: v }));
    fetchExactPrice(selectedPartId, v);
  };

  // MSF = (L × W) / 144 × Quantity (computed when part + qty known)
  const computeMsf = (partNumber, qty) => {
    const part = parts.find(p => p.part_number === partNumber);
    if (!part || !qty) return 0;
    const l = Number(part.length || 0), w = Number(part.width || 0);
    return ((l * w) / 144) * Number(qty);
  };

  const submit = async (e) => {
    e.preventDefault();
    setMsg('');
    const msf = computeMsf(form.part_number, form.qty_ordered);
    try {
      await api.post('/api/orders', { ...form, msf });
      setForm(empty);
      setMsg('Order created.');
    } catch { setMsg('Failed to create order.'); }
  };

  return (
    <div>
      <h2>Add Order</h2>
      <form onSubmit={submit} className="card">
        <div className="grid2">
          <div className="field"><label>Customer</label>
            <select value={form.customer_id} onChange={e => { upd('customer_id', e.target.value); upd('part_number', ''); setSelectedPartId(null); setPriceWarning(''); }} required>
              <option value="">Select…</option>{customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="field"><label>Part #</label>
            <select value={form.part_number} onChange={e => selectPart(e.target.value)} disabled={!form.customer_id} required>
              <option value="">{form.customer_id ? 'Select…' : 'Select customer first'}</option>
              {parts.map(p => <option key={p.id} value={p.part_number}>{p.part_number}{p.description ? ` — ${p.description}` : ''}</option>)}
            </select>
          </div>
          <div className="field"><label>PO #</label><input value={form.po_number} onChange={e => upd('po_number', e.target.value)} /></div>
          <div className="field"><label>Due Date</label><input type="date" value={form.due_date} onChange={e => upd('due_date', e.target.value)} /></div>
          <div className="field"><label>Machines</label>
            {(() => {
              const list = machines.length ? machines : MACHINES.map((n, i) => ({ id: i + 1, name: n }));
              const selectedIds = String(form.machine_id || '').split(',').filter(Boolean);
              const selectedNames = selectedIds.map(id => (list.find(m => String(m.id) === id) || {}).name).filter(Boolean);
              const toggle = (id) => {
                const s = new Set(selectedIds);
                if (s.has(id)) s.delete(id); else s.add(id);
                upd('machine_id', Array.from(s).join(','));
              };
              return (
                <>
                  <select multiple size={Math.min(list.length, 6)} value={selectedIds}
                    onChange={e => toggle(e.target.value)}
                    onMouseDown={e => { if (e.target.tagName === 'OPTION') { e.preventDefault(); toggle(e.target.value); e.target.parentElement.focus(); } }}>
                    {list.map(m => <option key={m.id} value={String(m.id)}>{m.name}</option>)}
                  </select>
                  <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>Selected: {selectedNames.join(', ') || 'none'}</div>
                </>
              );
            })()}
          </div>
          <div className="field"><label>Product Style</label><input value={form.product_style} onChange={e => upd('product_style', e.target.value)} /></div>
          <div className="field"><label>Qty Ordered</label><input type="number" value={form.qty_ordered} onChange={e => updQty(e.target.value)} /></div>
          <div className="field"><label>Qty Shipped</label><input type="number" value={form.qty_shipped} onChange={e => upd('qty_shipped', e.target.value)} /></div>
          {showPricing && (
            <div className="field">
              <label>Sales Price / Part</label>
              <input type="number" step="0.01" value={form.sales_price} onChange={e => upd('sales_price', e.target.value)} />
              {priceWarning && <div style={{ color: '#c0392b', fontSize: 12, marginTop: 4 }}>{priceWarning}</div>}
            </div>
          )}
          <div className="field"><label>Raw Material Status</label>
            <select value={form.raw_material_status} onChange={e => upd('raw_material_status', e.target.value)}>{MATERIAL_STATUSES.map(s => <option key={s}>{s}</option>)}</select>
          </div>
          <div className="field"><label>Order Status</label>
            <select value={form.status} onChange={e => upd('status', e.target.value)}>{STATUSES.map(s => <option key={s}>{s}</option>)}</select>
          </div>
        </div>
        <div className="field"><label>Notes / Remarks</label><textarea value={form.notes} onChange={e => upd('notes', e.target.value)} /></div>
        <button type="submit" className="btn-success">Create Order</button>
        {msg && <span style={{ marginLeft: 12, color: '#27ae60' }}>{msg}</span>}
      </form>
    </div>
  );
}
